import { FC, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { faSearch, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { SavedInstanceToolButton } from '@app/components/Record/RecordsTools/SavedInstanceToolButton';
import {
  ComponentSpinner,
  EmptyRecordsMessage,
  Icon,
  MoreToolsDropdown,
  RECORD_TOOLS,
  ResultCountWithSorting,
  TimestampedRecordWithData,
} from '@app/components';
import { useInstanceDetailsRoutePath } from '@app/hooks';
import { resultCountMessages, sharedMessages } from '@app/translations';
import { ROUTE } from '@app/constants';

import { useDashboardListSorting } from '../../useDashboardListSorting';
import { DashboardRecordCard } from '../DashboardRecordCard';

import '../HistoryRecordsPageWrapper.scss';

export type onRecordRemoval = (record: TimestampedRecordWithData) => void;

interface DashboardInstancesListProps {
  list: TimestampedRecordWithData[];
  emptyStateMessage: string;
  emptyStateIcon: IconDefinition;
  isLoading?: boolean;
  actionButton?: ReactElement;
  isItemRemovable?: boolean;
  onEntryRemoval: onRecordRemoval;
  onAddToProject: (id: string) => void;
}

const cnBem = createBemBlockBuilder(['emptyRecordsMessage']);

export const DashboardInstancesList: FC<DashboardInstancesListProps> = ({
  list = [],
  emptyStateMessage,
  emptyStateIcon,
  isLoading,
  actionButton,
  isItemRemovable,
  onEntryRemoval,
  onAddToProject,
}) => {
  const { getInstanceDetailsRoutePath } = useInstanceDetailsRoutePath();
  const { sortDirection, sortOptions, getSortedList, handleSortChange } =
    useDashboardListSorting();
  const navigate = useNavigate();
  const { $t } = useIntl();

  const getRecordTools = (record: TimestampedRecordWithData) => {
    const isNonExistingRecord = isEmpty(record.title);

    if (isNonExistingRecord) {
      return (
        <MoreToolsDropdown
          tools={[
            {
              ...RECORD_TOOLS.delete,
              onClick: () => onEntryRemoval(record),
            },
          ]}
        />
      );
    }

    return (
      <>
        <SavedInstanceToolButton
          entry={record}
          idKey="instanceId"
          isItemRemovable={isItemRemovable}
        />
        <MoreToolsDropdown
          tools={[
            {
              ...RECORD_TOOLS.addToProject,
              onClick: () => onAddToProject(record.instanceId),
            },
            RECORD_TOOLS.details,
            {
              ...RECORD_TOOLS.delete,
              onClick: () => onEntryRemoval(record),
            },
          ]}
        />
      </>
    );
  };

  const renderCurrentState = () => {
    if (isLoading) {
      return <ComponentSpinner />;
    }

    if (isEmpty(list)) {
      return (
        <EmptyRecordsMessage
          className={cnBem()}
          icon={emptyStateIcon}
          message={emptyStateMessage}
        >
          <Button onClick={() => navigate(ROUTE.search)}>
            <Icon icon={faSearch} />
            {$t(sharedMessages.startANewSearch)}
          </Button>
        </EmptyRecordsMessage>
      );
    }

    return (
      <>
        <ResultCountWithSorting
          recordsCount={list.length}
          countMessage={resultCountMessages.numberOfRecords}
          sortField="name"
          sortDirection={sortDirection}
          sortOptions={sortOptions}
          onSortChange={handleSortChange}
        />
        {getSortedList(list).map(record => (
          <DashboardRecordCard
            title={record.title}
            titleLink={getInstanceDetailsRoutePath(
              record.instanceId || record.id
            )}
            subTitle={record.sourceTypes?.join(', ')}
            contributors={record.contributors}
            timestamp={record.timestamp}
            publication={record.publication}
            key={record.id + record.timestamp}
            tools={getRecordTools(record)}
            {...(!isEmpty(record.title) && { actionButton })}
          />
        ))}
      </>
    );
  };

  return <div data-auto="instances-list">{renderCurrentState()}</div>;
};
